import { useQueries } from '@tanstack/react-query';
import { getDatesRange } from '@utils/helpers';
import { TablesAvailabilityType } from '../schemas/TablesAvailability.schema';
import { getTablesAvailability, getTablesAvailabilityKey } from './useGetTablesAvailability';
import { FIVE_MINUTES } from '@features/state/QueryClient/QueryClient';

interface UseGetTablesWeekAvailabilityParams {
  propertyId: number;
  startDate: string;
  restaurant: string;
  restaurantId: number | undefined;
  adults: number;
  children: number;
  enabled?: boolean;
}

export function useGetTablesWeekAvailability({
  propertyId,
  startDate,
  restaurant,
  restaurantId,
  adults,
  children,
  enabled = true,
}: UseGetTablesWeekAvailabilityParams) {
  const dates = getDatesRange(startDate);

  const queries = useQueries({
    queries: dates.map(({ date }) => ({
      queryKey: getTablesAvailabilityKey({
        propertyId,
        date: date.format('YYYY-MM-DD'),
        restaurant,
        restaurantId,
        adults,
        children,
      }),
      queryFn: () =>
        getTablesAvailability({
          propertyId,
          date: date.format('YYYY-MM-DD'),
          restaurant,
          restaurantId,
          adults,
          children,
        }),
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES,
      enabled: enabled && !!restaurant && !!restaurantId,
    })),
  });

  const isEnabled = enabled && !!restaurant && !!restaurantId;
  const isLoading = isEnabled && queries.some((query) => query.isFetching);
  const isError = queries.some((query) => query.isError);
  const errors = queries.filter((query) => query.error).map((query) => query.error);

  // Combine all the data into a single object keyed by date
  const data: Record<string, TablesAvailabilityType | undefined> = {};
  queries.forEach((query, index) => {
    const dateStr = dates[index].date.format('YYYY-MM-DD');
    // query.data can be undefined if restaurantId or restaurant is missing
    data[dateStr] = query.data as TablesAvailabilityType | undefined;
  });

  return {
    data,
    isLoading,
    isError,
    errors,
    queries, // Expose individual queries if needed
  };
}
